import { PrimaryButton, SecondaryButton } from './ui';
import { Button, Icon, type IconName } from '../ui';
import { StepFooter, StepHeading, type StepProps } from './SetupWizard';
import { audioLabel, featured, videoLabel } from './setup';

const TIPS: { t: string; d: string }[] = [
  { t: 'Test privately first', d: 'Make a YouTube stream Unlisted or a Twitch test with ?bandwidthtest=true before the real show.' },
  { t: 'Upload: 1.5× your bitrate', d: 'For 6 Mbps out of OBS keep at least 9 Mbps of upload free. Wired beats Wi-Fi.' },
  { t: 'Keyframe every 2 s, CBR', d: 'Platforms reject or buffer streams with long or variable keyframes.' },
  { t: 'Instagram is vertical', d: 'It shows 9:16. Keep faces and text in the centre third, or make a vertical scene in OBS.' },
  { t: 'Watch the cards, not the chat', d: 'Amber or red here means one platform has trouble; the others keep streaming.' },
  { t: 'Switch platforms live', d: 'Turn a destination off or on during the show: the others do not notice.' },
];

type Tone = 'ok' | 'warn' | 'mute';
/** the row's state icon (state is icon + word, never a glyph or colour alone) */
const MARK: Record<Tone, { icon: IconName; cls: string }> = {
  ok: { icon: 'state-live', cls: 'text-live' },
  warn: { icon: 'state-warn', cls: 'text-warn' },
  mute: { icon: 'state-idle', cls: 'text-idle' },
};
const TEXT: Record<Tone, string> = { ok: 'text-live', warn: 'text-warn', mute: 'text-muted' };

/** Step 3: what is ready, what still needs a key, and tips. Finish opens the dashboard. */
export function SetupStepDone({ view, profiles, dests, headingRef, go, finishing, onFinish }: StepProps & { finishing: boolean; onFinish: () => void }) {
  const input = view?.inputs.find((i) => i.live) ?? view?.inputs[0];
  // Connected = on air now or sent something in this broadcast.
  const obs = !!input?.live || (input?.bytes_received ?? 0) > 0;
  const obsDetail = input?.live
    ? [videoLabel(input.resolution, input.fps || input.declared_fps), input.bitrate_kbps > 0 ? `${(input.bitrate_kbps / 1000).toFixed(1)} Mbps` : audioLabel(input.audio_format)]
        .filter(Boolean)
        .join(' · ')
    : obs ? 'seen in this session' : 'start streaming in OBS when you are ready';
  const tiles = new Set(featured(profiles).map((p) => p.id));
  const rows = [...(dests ?? [])].sort((a, b) => a.id - b.id);

  return (
    <>
      <div className="grid gap-6 p-4 md:grid-cols-[minmax(0,1fr)_minmax(0,1.6fr)] md:gap-10 md:p-7">
        <div className="flex min-w-0 flex-col gap-3.5">
          <StepHeading headingRef={headingRef} sub="Next time just press Start Streaming in OBS.">You're all set</StepHeading>
          <ul className="m-0 list-none p-0" aria-label="Setup summary">
            <Item tone={obs ? 'ok' : 'mute'} name={obs ? 'OBS connected' : 'OBS not connected yet'} detail={obsDetail} testId="setup-row-obs" />
            {rows.map((d) => {
              const p = profiles?.find((x) => x.id === d.profile);
              const ready = !d.needs_key;
              return (
                <Item key={d.id} testId={`setup-row-${d.id}`} tone={ready ? 'ok' : 'warn'} name={d.name}
                  detail={ready ? (d.enabled ? 'ready, goes live with your stream' : 'ready, turned off') : 'needs a key, add it before going live'}
                  note={p?.go_live_note}
                  action={!ready && tiles.has(d.profile) ? { label: 'Add key', onClick: () => go(2, d.profile) } : undefined} />
              );
            })}
            {!rows.length && <Item tone="mute" name="No destinations yet" detail="add them later from Add output" />}
          </ul>
        </div>
        <div className="flex min-w-0 flex-col gap-3">
          <span className="sx-label">Tips for a smooth stream</span>
          <div className="grid gap-3 sm:grid-cols-2">
            {TIPS.map((x) => (
              <div key={x.t} className="flex flex-col gap-1.5 rounded-lg border border-line bg-bg p-3.5">
                <span className="font-medium">{x.t}</span>
                <span className="text-[13px] text-text-soft">{x.d}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <StepFooter
        left={<SecondaryButton className="max-md:min-h-[44px]" onClick={() => go(2)}>Back</SecondaryButton>}
        hint="Opens the dashboard"
        primary={
          <PrimaryButton testId="setup-confirm" className="max-md:min-h-[44px]" busy={finishing} onClick={onFinish}>
            Finish
          </PrimaryButton>
        }
      />
    </>
  );
}

function Item({ tone, name, detail, note, action, testId }: {
  tone: Tone; name: string; detail: string; note?: string; action?: { label: string; onClick: () => void }; testId?: string;
}) {
  return (
    <li data-testid={testId} data-tone={tone} className="flex flex-col gap-1 border-t border-line py-2.5">
      <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
        <Icon name={MARK[tone].icon} size={16} className={MARK[tone].cls} />
        <span className={`min-w-0 max-w-full truncate font-medium ${tone === 'mute' ? 'text-muted' : ''}`}>{name}</span>
        <span className={`text-[13px] ${TEXT[tone]}`}>{detail}</span>
        {action && (
          <Button size="sm" onClick={action.onClick} className="max-md:min-h-[44px]">
            <Icon name="key" size={16} />{action.label}
          </Button>
        )}
      </div>
      {note && <p data-testid="go-live-note" className="m-0 pl-[26px] text-xs text-muted">{note}</p>}
    </li>
  );
}
